import React, { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Maximize2, X } from "lucide-react";
import { useSiteContent } from "@/context/SiteContentContext";
import { resolveAssetUrl } from "@/lib/api";

export default function MenuViewer() {
  const { content } = useSiteContent();
  const flip = content?.menu_flipbook || {};
  const PAGES = (flip.pages && flip.pages.length ? flip.pages : []).map(resolveAssetUrl);

  const [i, setI] = useState(0);
  const [dir, setDir] = useState(1);
  const [zoom, setZoom] = useState(false);

  const goto = (n, d) => {
    if (!PAGES.length) return;
    setDir(d);
    setI((n + PAGES.length) % PAGES.length);
  };
  const prev = () => goto(i - 1, -1);
  const next = () => goto(i + 1, 1);

  const wrapRef = useRef(null);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });
  const onMove = (e) => {
    const r = wrapRef.current.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    const ry = ((x / r.width) - 0.5) * 14;
    const rx = ((y / r.height) - 0.5) * -10;
    setTilt({ rx, ry });
  };
  const onLeave = () => setTilt({ rx: 0, ry: 0 });

  return (
    <section id="menu" data-testid="menu-viewer-section" className="relative section-pad bg-bg overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-10 md:mb-16">
          <div className="md:col-span-8">
            <p className="text-xs tracking-[0.4em] uppercase text-[color:var(--gold-hi)]">{flip.eyebrow}</p>
            <h2 className="mt-6 font-serif-display text-5xl md:text-7xl leading-[1.02] text-cream">
              {flip.title_line_1} <br />
              <span className="italic gold-ink">{flip.title_line_2}</span>
            </h2>
          </div>
          <div className="md:col-span-4">
            <p className="text-cream/75 leading-relaxed">{flip.description}</p>
          </div>
        </div>

        {PAGES.length === 0 ? (
          <div className="text-center text-cream/60 italic py-20">No menu pages uploaded yet.</div>
        ) : (
          <div className="relative">
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="w-[70%] md:w-[55%] aspect-[1236/1749] rounded-2xl bg-[color:var(--bg-card)] opacity-40 translate-y-6 blur-[2px]" />
            </div>
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="w-[80%] md:w-[62%] aspect-[1236/1749] rounded-2xl bg-[color:var(--bg-card)] opacity-70 translate-y-3" />
            </div>

            <div className="relative flex items-center justify-center">
              <button onClick={prev} data-testid="menu-prev-btn" aria-label="Previous page"
                className="hidden md:flex absolute left-0 z-20 w-14 h-14 rounded-full bg-[rgba(26,14,8,0.7)] backdrop-blur border border-warm text-cream hover:bg-[color:var(--gold)] hover:text-[color:var(--bg)] transition items-center justify-center">
                <ChevronLeft size={22} />
              </button>

              <div
                ref={wrapRef}
                onMouseMove={onMove}
                onMouseLeave={onLeave}
                onClick={() => setZoom(true)}
                className="relative w-[90%] md:w-[70%] max-w-[720px] aspect-[1236/1749] cursor-zoom-in"
                style={{ perspective: "1600px" }}
                data-testid="menu-page-stage"
              >
                <AnimatePresence initial={false} custom={dir} mode="wait">
                  <motion.div
                    key={i}
                    custom={dir}
                    initial={{ opacity: 0, rotateY: dir * 45, x: dir * 120, scale: 0.9 }}
                    animate={{ opacity: 1, rotateY: 0, x: 0, scale: 1 }}
                    exit={{ opacity: 0, rotateY: -dir * 45, x: -dir * 120, scale: 0.9 }}
                    transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 rounded-2xl overflow-hidden shadow-gold ring-1 ring-warm"
                    style={{
                      transform: `rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)`,
                      transformStyle: "preserve-3d",
                      transition: "transform 0.4s cubic-bezier(0.22,1,0.36,1)",
                    }}
                  >
                    <img src={PAGES[i]} alt={`Menu page ${i + 1}`} className="w-full h-full object-cover" draggable={false} />
                    <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(120deg,rgba(255,255,255,0.14),transparent_40%,transparent_60%,rgba(0,0,0,0.25))]" />
                    <div className="absolute bottom-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-[rgba(26,14,8,0.7)] backdrop-blur border border-warm text-cream/90 text-[10px] tracking-[0.25em] uppercase">
                      <Maximize2 size={12} /> Tap to zoom
                    </div>
                    <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-[rgba(26,14,8,0.7)] backdrop-blur border border-warm text-cream/90 text-[10px] tracking-[0.35em] uppercase" data-testid="menu-index-indicator">
                      {String(i + 1).padStart(2, "0")} / {String(PAGES.length).padStart(2, "0")}
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              <button onClick={next} data-testid="menu-next-btn" aria-label="Next page"
                className="hidden md:flex absolute right-0 z-20 w-14 h-14 rounded-full bg-[color:var(--gold)] text-[color:var(--bg)] hover:bg-[color:var(--gold-hi)] transition items-center justify-center ember-glow">
                <ChevronRight size={22} />
              </button>
            </div>

            <div className="md:hidden mt-6 flex items-center justify-center gap-4">
              <button onClick={prev} data-testid="menu-prev-btn-mobile" aria-label="Previous page"
                className="w-12 h-12 rounded-full bg-[rgba(26,14,8,0.7)] border border-warm text-cream flex items-center justify-center">
                <ChevronLeft size={20} />
              </button>
              <button onClick={next} data-testid="menu-next-btn-mobile" aria-label="Next page"
                className="w-12 h-12 rounded-full bg-[color:var(--gold)] text-[color:var(--bg)] flex items-center justify-center ember-glow">
                <ChevronRight size={20} />
              </button>
            </div>

            <div className="mt-10 flex justify-center flex-wrap gap-2">
              {PAGES.map((_, k) => (
                <button
                  key={k}
                  onClick={() => goto(k, k > i ? 1 : -1)}
                  data-testid={`menu-dot-${k}`}
                  aria-label={`Go to page ${k + 1}`}
                  className={`h-1.5 rounded-full transition-all ${k === i ? "w-10 bg-[color:var(--gold)]" : "w-4 bg-warm hover:bg-[color:var(--gold-lo)]"}`}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {zoom && PAGES.length > 0 && (
          <motion.div
            key="zoom"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setZoom(false)}
            className="fixed inset-0 z-[200] bg-[rgba(15,8,4,0.94)] backdrop-blur-xl flex items-center justify-center p-4 md:p-10 cursor-zoom-out"
            data-testid="menu-zoom-modal"
          >
            <button onClick={(e) => { e.stopPropagation(); setZoom(false); }} data-testid="menu-zoom-close"
              className="absolute top-4 right-4 md:top-6 md:right-6 w-12 h-12 rounded-full bg-[color:var(--bg-card)] border border-warm text-cream hover:bg-[color:var(--gold)] hover:text-[color:var(--bg)] transition flex items-center justify-center z-10" aria-label="Close">
              <X size={20} />
            </button>

            <button onClick={(e) => { e.stopPropagation(); prev(); }} data-testid="menu-zoom-prev"
              className="absolute left-2 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 rounded-full bg-[color:var(--bg-card)] border border-warm text-cream hover:bg-[color:var(--gold)] hover:text-[color:var(--bg)] transition flex items-center justify-center z-10" aria-label="Previous">
              <ChevronLeft size={22} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); next(); }} data-testid="menu-zoom-next"
              className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 rounded-full bg-[color:var(--gold)] text-[color:var(--bg)] hover:bg-[color:var(--gold-hi)] transition flex items-center justify-center z-10" aria-label="Next">
              <ChevronRight size={22} />
            </button>

            <motion.img
              key={i}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              src={PAGES[i]}
              alt={`Menu page ${i + 1} enlarged`}
              className="max-h-full max-w-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />

            <div className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-[color:var(--bg-card)] border border-warm text-cream/90 text-xs tracking-[0.35em] uppercase">
              {String(i + 1).padStart(2, "0")} / {String(PAGES.length).padStart(2, "0")}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
